// One browse card: identity, the first-party badge, and the access badge
// (reused from Part 3). The whole card is a button that opens the detail page.

import type { ReactElement } from 'react';
import { BadgeCheck } from 'lucide-react';
import type { PluginSummary } from './source';
import { Tooltip } from './Tooltip';

export interface PluginCardProps {
  summary: PluginSummary;
  /** Optional status pill shown on the Installed tab (e.g. 'Enabled'/'Disabled'). */
  status?: string;
  onOpen(id: string): void;
}

export function PluginCard({ summary, status, onOpen }: PluginCardProps): ReactElement {
  const b = summary.accessBadge;
  return (
    <button
      type="button"
      className="mp-pcard"
      onClick={() => onOpen(summary.id)}
      aria-label={`${summary.name} by ${summary.publisher}`}
    >
      <div className="mp-pcard__head">
        <h3 className="mp-pcard__name">{summary.name}</h3>
        {summary.firstParty && (
          <Tooltip label="Official">
            <span className="mp-pcard__official" aria-label="Official">
              <BadgeCheck size={15} aria-hidden />
            </span>
          </Tooltip>
        )}
        {/* Calculated risk as a colored dot after the name; the full breakdown
            lives on the detail page. A no-access plugin shows no dot. */}
        {b.level !== 'none' && (
          <Tooltip label={b.label}>
            <span
              className="mp-pcard__risk"
              data-level={b.level}
              aria-label={`Access level: ${b.label}`}
            />
          </Tooltip>
        )}
        {status && <span className="mp-pcard__status" data-status={status}>{status}</span>}
      </div>
      <p className="mp-pcard__publisher">{summary.publisher}</p>
      {summary.description && <p className="mp-pcard__desc">{summary.description}</p>}
    </button>
  );
}
